import { RiAddLine } from "@remixicon/react";
import classNames from "classnames";
import React, {
    useEffect, useMemo, useRef, useState,
} from "react";
import {
    User,
    useGetConversationUsersQuery,
} from "../../../../../__generated__/api";
import Confirm from "../../../../confirm/Confirm";
import Users from "../../../../pickers/users/Users";
import Popover, { PopoverController } from "../../../../popover/Popover";
import Button from "../../../../theme/button/Button";
import FormGroup from "../../../../theme/form-group/FormGroup";
import UserDisplay from "../../../labels/user/User";
import UserList from "../../../user-list/UserList";
import Styles from "../TicketDetails.module.scss";
import {
    DetailRow,
    DetailSection,
    TicketProperties, sectionRegistry,
} from "./base";

export default function GeneralSection({
    ticket, isMinimized, onToggleExpand, metricPathPrefix,
}: TicketProperties) {
    const { data: usersData, loading: isLoadingUsers } = useGetConversationUsersQuery({
        variables: {
            id: ticket.chat.source?.id,
        },
        skip: ticket.chat.source?.id == null,
    });

    const { isLoading: isLoadingParticipants } = ticket.useParticipants();

    const [shouldShowRelated, setShouldShowRelated] = useState(false);
    const [deletingParticipant, setDeletingParticipant] = useState<User>();
    const [deleteReason, setDeleteReason] = useState<string>();

    const metricPath = useMemo(() => [...metricPathPrefix, "people"], metricPathPrefix);

    useEffect(() => {
        setDeleteReason("");
    }, [deletingParticipant]);
    const popperControllerRef = useRef<PopoverController>();

    ticket.useChatDetails();

    return (
        <DetailSection title="People" isMinimized={isMinimized} onToggleExpand={onToggleExpand}>
            <Confirm
                shouldShow={deletingParticipant != null}
                closeText="Cancel"
                confirmText="Remove"
                onHide={() => setDeletingParticipant(undefined)}
                title={`Remove ${deletingParticipant?.name}?`}
                confirmMetricPath={[...metricPath, "participant", "remove"]}
                onConfirm={() => {
                    setDeletingParticipant(undefined);
                    ticket.removeParticipants([deletingParticipant?.id], deleteReason);
                }}
                confirmVariant="danger"
            >
                <FormGroup
                    type="text"
                    display="textarea"
                    label="Notes"
                    isOptional
                    placeholder={`I am removing ${deletingParticipant?.firstName} because...`}
                    value={deleteReason}
                    onChange={evt => setDeleteReason(evt.target.value)}
                    helpText="Would you like to add any private notes why the participant is being removed?"
                />

            </Confirm>
            <DetailRow title="Related" className={classNames(Styles["people-list"], isLoadingUsers ? "loading" : "")}>
                <UserList
                    users={usersData?.conversationSourceUsers as User[] ?? []}
                    shouldShow={shouldShowRelated}
                    title={`Related People (${usersData?.conversationSourceUsers?.length})`}
                    onHide={() => setShouldShowRelated(false)}
                    metricPathPrefix={[...metricPath, "participant", "related"]}
                />
                {isLoadingUsers ? (
                    <>
                        <div className={Styles["people-list-item"]}>
                            <UserDisplay
                                allowOverlay={false}
                                user={{ name: "Sarah Langa" } as User}
                            />
                        </div>
                        <div className={Styles["people-list-item"]}>
                            <UserDisplay
                                allowOverlay={false}
                                user={{ name: "Sarah Langa" } as User}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        {usersData?.conversationSourceUsers?.limit(5).map(user => (
                            <UserDisplay metricPathPrefix={metricPath} key={user.id} user={user as User} />
                        ))}
                        {usersData?.conversationSourceUsers
                            && usersData?.conversationSourceUsers?.length > 5
                            && (
                                <Button
                                    variant="none"
                                    className="show-all"
                                    onClick={() => setShouldShowRelated(true)}
                                    metricPath={[...metricPath, "show-all"]}
                                >
                                    {`Show all (${usersData.conversationSourceUsers.length})`}
                                </Button>
                            )}
                    </>
                )}

            </DetailRow>

            <DetailRow
                title="Participants"
                className={classNames(Styles["people-list"], isLoadingParticipants ? "loading" : "")}
            >
                {isLoadingParticipants ? (
                    <>
                        <div className={Styles["people-list-item"]}>
                            <UserDisplay
                                allowOverlay={false}
                                user={{ name: "Sarah Langa" } as User}
                            />
                        </div>
                        <div className={Styles["people-list-item"]}>
                            <UserDisplay
                                allowOverlay={false}
                                user={{ name: "Sarah Langa" } as User}
                            />
                        </div>
                    </>
                ) : (

                    ticket?.participants?.map(user => (
                        <div key={user.id} className={Styles["people-list-item"]}>
                            <div className={Styles["people-list-item-name"]}>
                                <UserDisplay
                                    key={user.id}
                                    user={user as User}
                                    metricPathPrefix={[...metricPath, "participant"]}
                                />
                            </div>
                            <Button
                                size="xsmall"
                                variant="none"
                                className={Styles["people-list-delete"]}
                                isClose
                                onClick={() => setDeletingParticipant(user)}
                            />

                        </div>
                    ))

                )}

                <Popover
                    title="Add Participant"
                    placement="left"
                    buttonClassName="btn-none show-all"
                    popoverClassName={Styles.popover}
                    ref={popperControllerRef}
                    text={(
                        <>
                            <RiAddLine />
                            {" "}
                            Add Participant
                        </>
                    )}
                >
                    <Users
                        shouldShowMenu
                        isMulti
                        onLoaded={() => {
                            popperControllerRef.current?.scheduleUpdate();
                        }}
                        shouldShowSelfFirst
                        className={Styles["user-menu"]}
                        value={ticket.participants?.toIdList("id")}
                        onSelected={ids => {
                            popperControllerRef.current?.hide();
                            const current = ticket?.participants?.toIdList("id") ?? [];
                            const added = ids.exclude(current);
                            const removed = current.exclude(ids);
                            ticket.addParticipants(added);
                            ticket.removeParticipants(removed);
                            // onChange?.(userId);
                        }}
                    />

                </Popover>

            </DetailRow>

        </DetailSection>

    );
}

sectionRegistry.register({
    sectionId: "urn:fq:portal-chat-section:people",
    label: "People",
    component: GeneralSection,
});
